import { create } from 'zustand'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import {
  db,
  generateId,
  nowISO,
  queueSync,
  saveActiveWorkoutState,
  getActiveWorkoutState,
  clearActiveWorkoutState,
  type LocalWorkoutSession,
  type LocalExerciseLog,
  type LocalSetLog,
} from '@/lib/db'
import {
  getWorkoutTemplate,
  getWeekProgression,
  isDeloadWeek,
  VOLUME_TIER_SCHEDULES,
  PHASE_1_TEMPLATES,
  PHASE_2_TEMPLATES,
  type WorkoutType,
  type ExerciseSlot,
  type Phase,
  type Week,
  type VolumeTier,
} from '@/data/xframe-templates'

// Active exercise state during workout
export interface ActiveExercise {
  slotId: string
  exerciseLogId: string
  name: string
  alternatives: string[]
  targetSets: number
  reps: string
  isCompound: boolean
  notes?: string
  sets: ActiveSet[]
}

export interface ActiveSet {
  id: string
  setNumber: number
  weightKg: number | null
  reps: number | null
  rir: number | null
  setType: 'working' | 'warmup' | 'rest_pause' | 'drop'
  completed: boolean
}

// Saved workout info for resume modal
export interface SavedWorkoutInfo {
  workoutType: string
  displayName: string
  phase: 1 | 2
  week: number
  startTime: number
  exercisesCompleted: number
  totalExercises: number
  lastSavedAt: string
}

interface WorkoutState {
  // Session data
  sessions: LocalWorkoutSession[]
  loading: boolean
  error: string | null

  // Active workout state
  activeSession: LocalWorkoutSession | null
  activeExercises: ActiveExercise[]
  currentExerciseIndex: number
  workoutStartTime: number | null
  restTimerEnd: number | null

  // Resume state
  isResuming: boolean
  savedWorkoutInfo: SavedWorkoutInfo | null

  // Actions - Session management
  loadSessions: (userId: string, limit?: number) => Promise<void>
  getRecentSessions: (limit?: number) => LocalWorkoutSession[]
  getSessionsByDateRange: (startDate: string, endDate: string) => LocalWorkoutSession[]
  getCompletedDatesThisWeek: () => string[]

  // Actions - Active workout
  startWorkout: (
    userId: string,
    workoutType: WorkoutType,
    phase: Phase,
    week: Week,
    volumeTier: VolumeTier
  ) => Promise<void>
  cancelWorkout: () => Promise<void>
  finishWorkout: () => Promise<void>

  // Actions - Resume/Recovery
  checkForSavedWorkout: () => Promise<SavedWorkoutInfo | null>
  resumeWorkout: () => Promise<boolean>
  discardSavedWorkout: () => Promise<void>
  saveWorkoutState: () => Promise<void>

  // Actions - Exercise management
  setCurrentExercise: (index: number) => void
  substituteExercise: (exerciseIndex: number, newExerciseName: string) => void

  // Actions - Set logging
  updateSet: (exerciseIndex: number, setIndex: number, updates: Partial<ActiveSet>) => void
  completeSet: (exerciseIndex: number, setIndex: number) => void
  addSet: (exerciseIndex: number, setType?: 'working' | 'rest_pause' | 'drop') => void

  // Actions - Rest timer
  startRestTimer: (seconds: number) => void
  clearRestTimer: () => void

  // Actions - Exercise history & PRs
  getExerciseHistory: (exerciseName: string, limit?: number) => Promise<ExerciseHistoryEntry[]>
  getExercisePR: (exerciseName: string) => Promise<PRRecord | null>
  getAllPRs: () => Promise<PRRecord[]>
}

// Types for exercise history and PRs
export interface ExerciseHistoryEntry {
  date: string
  sessionId: string
  sets: {
    setNumber: number
    weightKg: number
    reps: number
    rir?: number
    setType: string
    isPr: boolean
  }[]
  bestSet: {
    weightKg: number
    reps: number
    estimatedOneRM: number
  }
}

export interface PRRecord {
  exerciseName: string
  weightKg: number
  reps: number
  estimatedOneRM: number
  date: string
  sessionId: string
}

// Helper to get workout display name from type
function getWorkoutDisplayName(workoutType: string, phase: Phase): string {
  const templates = phase === 1 ? PHASE_1_TEMPLATES : PHASE_2_TEMPLATES
  return templates[workoutType as WorkoutType]?.displayName || workoutType
}

export const useWorkoutStore = create<WorkoutState>()((set, get) => ({
  sessions: [],
  loading: false,
  error: null,
  activeSession: null,
  activeExercises: [],
  currentExerciseIndex: 0,
  workoutStartTime: null,
  restTimerEnd: null,
  isResuming: false,
  savedWorkoutInfo: null,

  loadSessions: async (userId: string, limit = 50) => {
    set({ loading: true, error: null })

    try {
      // Always load from IndexedDB first (offline-first approach)
      const localSessions = await db.workoutSessions
        .where('user_id')
        .equals(userId)
        .toArray()

      // Sort by date descending
      localSessions.sort((a, b) => b.date.localeCompare(a.date))

      // Set initial state from local data immediately
      set({ sessions: localSessions.slice(0, limit), loading: false })

      // Then sync with Supabase in background if configured
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from('workout_sessions')
            .select('*')
            .eq('user_id', userId)
            .order('date', { ascending: false })
            .limit(limit)

          if (!error && data && data.length > 0) {
            const remoteSessions: LocalWorkoutSession[] = data.map((row) => ({
              id: row.id,
              user_id: row.user_id,
              date: row.date,
              workout_type: row.workout_type,
              phase: row.phase as 1 | 2,
              week: row.week,
              volume_tier: row.volume_tier as 1 | 2 | 3,
              is_deload: row.is_deload,
              total_volume_kg: row.total_volume_kg ? parseFloat(row.total_volume_kg) : undefined,
              duration_minutes: row.duration_minutes ?? undefined,
              notes: row.notes ?? undefined,
              completed: row.completed,
              sync_status: 'synced' as const,
              created_at: row.created_at,
              updated_at: row.updated_at,
            }))

            // Merge: keep local pending items not on remote
            const pendingLocal = localSessions.filter((s) => s.sync_status === 'pending')
            const remoteIds = new Set(remoteSessions.map((s) => s.id))
            const pendingNotOnRemote = pendingLocal.filter((s) => !remoteIds.has(s.id))

            // Combine remote sessions with any pending local-only sessions
            const mergedSessions = [...remoteSessions, ...pendingNotOnRemote]
              .sort((a, b) => b.date.localeCompare(a.date))
              .slice(0, limit)

            // Cache remote sessions in IndexedDB
            await db.workoutSessions.bulkPut(remoteSessions)

            // Update state with merged data
            set({ sessions: mergedSessions })
          }
        } catch (syncError) {
          // Supabase sync failed, but we already have local data - that's fine
          console.warn('Supabase sync failed, using local data:', syncError)
        }
      }
    } catch (error) {
      console.error('Load workout sessions error:', error)
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load workouts',
      })
    }
  },

  getRecentSessions: (limit = 5) => {
    return get().sessions.filter((s) => s.completed).slice(0, limit)
  },

  getSessionsByDateRange: (startDate: string, endDate: string) => {
    return get().sessions.filter((s) => {
      const sessionDate = s.date.split('T')[0]
      return sessionDate >= startDate && sessionDate <= endDate
    })
  },

  getCompletedDatesThisWeek: () => {
    const now = new Date()
    const dayOfWeek = now.getDay()
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    const monday = new Date(now)
    monday.setDate(now.getDate() + mondayOffset)
    monday.setHours(0, 0, 0, 0)

    const mondayStr = monday.toISOString().split('T')[0]
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    const sundayStr = sunday.toISOString().split('T')[0]

    return get()
      .sessions.filter((s) => {
        const sessionDate = s.date.split('T')[0]
        return s.completed && sessionDate >= mondayStr && sessionDate <= sundayStr
      })
      .map((s) => s.date.split('T')[0])
  },

  startWorkout: async (userId, workoutType, phase, week, volumeTier) => {
    const now = nowISO()
    const sessionId = generateId()
    const isDeload = isDeloadWeek(week)
    const template = getWorkoutTemplate(phase, workoutType)
    const progression = getWeekProgression(phase, week)

    // Create session
    const session: LocalWorkoutSession = {
      id: sessionId,
      user_id: userId,
      date: now,
      workout_type: workoutType,
      phase,
      week,
      volume_tier: volumeTier,
      is_deload: isDeload,
      completed: false,
      sync_status: 'pending',
      created_at: now,
      updated_at: now,
    }

    // Build active exercises from template
    const activeExercises: ActiveExercise[] = template.exercises.map((slot: ExerciseSlot) => {
      const exerciseLogId = generateId()
      const targetSets = isDeload ? Math.ceil(slot.sets / 2) : slot.sets
      const targetRir = slot.isCompound ? progression.rir.compound : progression.rir.isolation

      // Pre-populate sets
      const sets: ActiveSet[] = Array.from({ length: targetSets }, (_, i) => ({
        id: generateId(),
        setNumber: i + 1,
        weightKg: null,
        reps: null,
        rir: targetRir,
        setType: 'working' as const,
        completed: false,
      }))

      return {
        slotId: slot.id,
        exerciseLogId,
        name: slot.name,
        alternatives: slot.alternatives,
        targetSets,
        reps: slot.reps,
        isCompound: slot.isCompound,
        notes: slot.notes,
        sets,
      }
    })

    // Save to IndexedDB
    await db.workoutSessions.add(session)

    const startTime = Date.now()

    set({
      activeSession: session,
      activeExercises,
      currentExerciseIndex: 0,
      workoutStartTime: startTime,
      restTimerEnd: null,
    })

    // Save initial workout state for resume capability
    await saveActiveWorkoutState({
      user_id: userId,
      session_id: sessionId,
      workout_type: workoutType,
      phase,
      week,
      volume_tier: volumeTier,
      is_deload: isDeload,
      start_time: startTime,
      current_exercise_index: 0,
      exercises_json: JSON.stringify(activeExercises),
    })
  },

  cancelWorkout: async () => {
    const { activeSession } = get()
    if (activeSession) {
      // Delete the session from IndexedDB
      await db.workoutSessions.delete(activeSession.id)
    }

    // Clear saved workout state
    await clearActiveWorkoutState()

    set({
      activeSession: null,
      activeExercises: [],
      currentExerciseIndex: 0,
      workoutStartTime: null,
      restTimerEnd: null,
      savedWorkoutInfo: null,
    })
  },

  finishWorkout: async () => {
    const { activeSession, activeExercises, workoutStartTime, sessions } = get()
    if (!activeSession) return

    const now = nowISO()
    const durationMinutes = workoutStartTime
      ? Math.round((Date.now() - workoutStartTime) / 60000)
      : undefined

    // Calculate total volume
    let totalVolumeKg = 0
    const exerciseLogs: LocalExerciseLog[] = []
    const setLogs: LocalSetLog[] = []

    activeExercises.forEach((exercise, exerciseIndex) => {
      const exerciseLog: LocalExerciseLog = {
        id: exercise.exerciseLogId,
        session_id: activeSession.id,
        exercise_id: exercise.name, // Use exercise name for PR tracking
        slot_identifier: exercise.slotId,
        order_index: exerciseIndex,
        notes: exercise.notes,
        sync_status: 'pending',
        created_at: now,
      }
      exerciseLogs.push(exerciseLog)

      exercise.sets.forEach((set) => {
        if (set.completed && set.weightKg && set.reps) {
          if (set.setType === 'working') {
            totalVolumeKg += set.weightKg * set.reps
          }

          const setLog: LocalSetLog = {
            id: set.id,
            exercise_log_id: exercise.exerciseLogId,
            set_number: set.setNumber,
            weight_kg: set.weightKg,
            reps: set.reps,
            rir: set.rir ?? undefined,
            set_type: set.setType,
            is_pr: false, // TODO: Implement PR detection
            sync_status: 'pending',
            created_at: now,
          }
          setLogs.push(setLog)
        }
      })
    })

    // Update session
    const updatedSession: LocalWorkoutSession = {
      ...activeSession,
      completed: true,
      total_volume_kg: totalVolumeKg,
      duration_minutes: durationMinutes,
      updated_at: now,
    }

    // Save everything to IndexedDB
    await db.workoutSessions.put(updatedSession)
    await db.exerciseLogs.bulkAdd(exerciseLogs)
    await db.setLogs.bulkAdd(setLogs)

    // Try to sync to Supabase
    if (isSupabaseConfigured()) {
      try {
        await supabase.from('workout_sessions').upsert({
          id: updatedSession.id,
          user_id: updatedSession.user_id,
          date: updatedSession.date,
          workout_type: updatedSession.workout_type,
          phase: updatedSession.phase,
          week: updatedSession.week,
          volume_tier: updatedSession.volume_tier,
          is_deload: updatedSession.is_deload,
          total_volume_kg: totalVolumeKg,
          duration_minutes: durationMinutes,
          completed: true,
        })

        await db.workoutSessions.update(updatedSession.id, { sync_status: 'synced' })
      } catch (error) {
        console.error('Sync workout error:', error)
        await queueSync('workout_sessions', updatedSession.id, 'update')
      }
    }

    // Clear saved workout state
    await clearActiveWorkoutState()

    // Update local state
    set({
      sessions: [updatedSession, ...sessions],
      activeSession: null,
      activeExercises: [],
      currentExerciseIndex: 0,
      workoutStartTime: null,
      restTimerEnd: null,
      savedWorkoutInfo: null,
    })
  },

  // Resume/Recovery actions
  checkForSavedWorkout: async () => {
    try {
      const savedState = await getActiveWorkoutState()
      if (!savedState) {
        set({ savedWorkoutInfo: null })
        return null
      }

      const exercises: ActiveExercise[] = JSON.parse(savedState.exercises_json)
      const completedCount = exercises.filter((ex) =>
        ex.sets.every((s) => s.completed)
      ).length

      const info: SavedWorkoutInfo = {
        workoutType: savedState.workout_type,
        displayName: getWorkoutDisplayName(savedState.workout_type, savedState.phase),
        phase: savedState.phase,
        week: savedState.week,
        startTime: savedState.start_time,
        exercisesCompleted: completedCount,
        totalExercises: exercises.length,
        lastSavedAt: savedState.last_saved_at,
      }

      set({ savedWorkoutInfo: info })
      return info
    } catch (error) {
      console.error('Check for saved workout error:', error)
      return null
    }
  },

  resumeWorkout: async () => {
    try {
      set({ isResuming: true })

      const savedState = await getActiveWorkoutState()
      if (!savedState) {
        set({ isResuming: false })
        return false
      }

      // Get the session from IndexedDB
      const session = await db.workoutSessions.get(savedState.session_id)
      if (!session) {
        // Session was deleted, clear the saved state
        await clearActiveWorkoutState()
        set({ isResuming: false, savedWorkoutInfo: null })
        return false
      }

      // Parse the saved exercises
      const exercises: ActiveExercise[] = JSON.parse(savedState.exercises_json)

      // Restore the workout state
      set({
        activeSession: session,
        activeExercises: exercises,
        currentExerciseIndex: savedState.current_exercise_index,
        workoutStartTime: savedState.start_time,
        restTimerEnd: savedState.rest_timer_end ?? null,
        isResuming: false,
        savedWorkoutInfo: null,
      })

      return true
    } catch (error) {
      console.error('Resume workout error:', error)
      set({ isResuming: false })
      return false
    }
  },

  discardSavedWorkout: async () => {
    try {
      const savedState = await getActiveWorkoutState()
      if (savedState) {
        // Also delete the incomplete session from IndexedDB
        await db.workoutSessions.delete(savedState.session_id)
      }
      await clearActiveWorkoutState()
      set({ savedWorkoutInfo: null })
    } catch (error) {
      console.error('Discard saved workout error:', error)
    }
  },

  saveWorkoutState: async () => {
    const { activeSession, activeExercises, currentExerciseIndex, workoutStartTime, restTimerEnd } = get()
    if (!activeSession || !workoutStartTime) return

    try {
      await saveActiveWorkoutState({
        user_id: activeSession.user_id,
        session_id: activeSession.id,
        workout_type: activeSession.workout_type,
        phase: activeSession.phase,
        week: activeSession.week,
        volume_tier: activeSession.volume_tier,
        is_deload: activeSession.is_deload,
        start_time: workoutStartTime,
        current_exercise_index: currentExerciseIndex,
        exercises_json: JSON.stringify(activeExercises),
        rest_timer_end: restTimerEnd ?? undefined,
      })
    } catch (error) {
      console.error('Save workout state error:', error)
    }
  },

  setCurrentExercise: (index: number) => {
    set({ currentExerciseIndex: index })
    // Auto-save on exercise change
    get().saveWorkoutState()
  },

  substituteExercise: (exerciseIndex: number, newExerciseName: string) => {
    set((state) => ({
      activeExercises: state.activeExercises.map((ex, i) =>
        i === exerciseIndex ? { ...ex, name: newExerciseName } : ex
      ),
    }))
    // Auto-save on substitution
    get().saveWorkoutState()
  },

  updateSet: (exerciseIndex: number, setIndex: number, updates: Partial<ActiveSet>) => {
    set((state) => ({
      activeExercises: state.activeExercises.map((ex, i) =>
        i === exerciseIndex
          ? {
              ...ex,
              sets: ex.sets.map((s, j) => (j === setIndex ? { ...s, ...updates } : s)),
            }
          : ex
      ),
    }))
    // Note: We don't auto-save on every keystroke - that's handled by debounce in the UI
  },

  completeSet: (exerciseIndex: number, setIndex: number) => {
    set((state) => ({
      activeExercises: state.activeExercises.map((ex, i) =>
        i === exerciseIndex
          ? {
              ...ex,
              sets: ex.sets.map((s, j) => (j === setIndex ? { ...s, completed: true } : s)),
            }
          : ex
      ),
    }))
    // Auto-save on set completion - this is a critical save point
    get().saveWorkoutState()
  },

  addSet: (exerciseIndex: number, setType = 'working') => {
    set((state) => {
      const exercise = state.activeExercises[exerciseIndex]
      if (!exercise) return state

      const newSet: ActiveSet = {
        id: generateId(),
        setNumber: exercise.sets.length + 1,
        weightKg: exercise.sets[exercise.sets.length - 1]?.weightKg || null,
        reps: null,
        rir: exercise.sets[exercise.sets.length - 1]?.rir ?? null,
        setType: setType as 'working' | 'rest_pause' | 'drop',
        completed: false,
      }

      return {
        activeExercises: state.activeExercises.map((ex, i) =>
          i === exerciseIndex ? { ...ex, sets: [...ex.sets, newSet] } : ex
        ),
      }
    })
  },

  startRestTimer: (seconds: number) => {
    set({ restTimerEnd: Date.now() + seconds * 1000 })
  },

  clearRestTimer: () => {
    set({ restTimerEnd: null })
  },

  getExerciseHistory: async (exerciseName: string, limit = 20): Promise<ExerciseHistoryEntry[]> => {
    try {
      // Get exercise logs matching the name
      const exerciseLogs = await db.exerciseLogs
        .where('exercise_id')
        .equals(exerciseName)
        .reverse()
        .sortBy('created_at')

      const limitedLogs = exerciseLogs.slice(0, limit)
      const history: ExerciseHistoryEntry[] = []

      for (const log of limitedLogs) {
        // Get the session date
        const session = await db.workoutSessions.get(log.session_id)
        if (!session) continue

        // Get all sets for this exercise log
        const sets = await db.setLogs
          .where('exercise_log_id')
          .equals(log.id)
          .sortBy('set_number')

        if (sets.length === 0) continue

        // Find best set by estimated 1RM (Epley formula: weight * (1 + reps/30))
        let bestSet = sets[0]
        let bestEstimated1RM = bestSet.weight_kg * (1 + bestSet.reps / 30)

        sets.forEach(s => {
          const estimated1RM = s.weight_kg * (1 + s.reps / 30)
          if (estimated1RM > bestEstimated1RM) {
            bestSet = s
            bestEstimated1RM = estimated1RM
          }
        })

        history.push({
          date: session.date,
          sessionId: session.id,
          sets: sets.map(s => ({
            setNumber: s.set_number,
            weightKg: s.weight_kg,
            reps: s.reps,
            rir: s.rir,
            setType: s.set_type,
            isPr: s.is_pr,
          })),
          bestSet: {
            weightKg: bestSet.weight_kg,
            reps: bestSet.reps,
            estimatedOneRM: bestEstimated1RM,
          },
        })
      }

      return history
    } catch (error) {
      console.error('Get exercise history error:', error)
      return []
    }
  },

  getExercisePR: async (exerciseName: string): Promise<PRRecord | null> => {
    try {
      // Get all exercise logs for this exercise
      const exerciseLogs = await db.exerciseLogs
        .where('exercise_id')
        .equals(exerciseName)
        .toArray()

      let bestPR: PRRecord | null = null
      let bestEstimated1RM = 0

      for (const log of exerciseLogs) {
        const session = await db.workoutSessions.get(log.session_id)
        if (!session) continue

        const sets = await db.setLogs
          .where('exercise_log_id')
          .equals(log.id)
          .toArray()

        for (const set of sets) {
          const estimated1RM = set.weight_kg * (1 + set.reps / 30)
          if (estimated1RM > bestEstimated1RM) {
            bestEstimated1RM = estimated1RM
            bestPR = {
              exerciseName,
              weightKg: set.weight_kg,
              reps: set.reps,
              estimatedOneRM: estimated1RM,
              date: session.date,
              sessionId: session.id,
            }
          }
        }
      }

      return bestPR
    } catch (error) {
      console.error('Get exercise PR error:', error)
      return null
    }
  },

  getAllPRs: async (): Promise<PRRecord[]> => {
    try {
      // Get all unique exercise names
      const exerciseLogs = await db.exerciseLogs.toArray()
      const exerciseNames = [...new Set(exerciseLogs.map(l => l.exercise_id))]

      const prs: PRRecord[] = []
      const store = get()

      for (const name of exerciseNames) {
        const pr = await store.getExercisePR(name)
        if (pr) prs.push(pr)
      }

      // Sort by estimated 1RM descending
      return prs.sort((a, b) => b.estimatedOneRM - a.estimatedOneRM)
    } catch (error) {
      console.error('Get all PRs error:', error)
      return []
    }
  },
}))

// Helper to get the next workout in the schedule
export function getNextWorkout(
  volumeTier: VolumeTier,
  completedWorkouts: WorkoutType[],
  phase: Phase
): { workoutType: WorkoutType; displayName: string } | null {
  const schedule = VOLUME_TIER_SCHEDULES[volumeTier].filter(
    (w: WorkoutType | null) => w !== null
  ) as WorkoutType[]
  const templates = phase === 1 ? PHASE_1_TEMPLATES : PHASE_2_TEMPLATES

  const completedCount = completedWorkouts.length
  const nextIndex = completedCount % schedule.length
  const nextWorkoutType = schedule[nextIndex]

  if (!nextWorkoutType) return null

  return {
    workoutType: nextWorkoutType,
    displayName: templates[nextWorkoutType]?.displayName || nextWorkoutType,
  }
}
